/**
 * CurrenciesManagementPage — list, search, filter currencies.
 * Standard management page pattern.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Coins,
  ChevronRight,
  CheckCircle,
  Star,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { useDeleteCurrency } from '../hooks';
import { localizedName } from '@/utils/localized';
import type { Currency, CurrencyListResponse } from '../types';
import { CurrencyFormModal } from './CurrencyFormModal';
import { CurrencyDetailModal } from './CurrencyDetailModal';
import toast from 'react-hot-toast';
import { useModal } from '@/components/modal';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  GlassSelect,
  CreateButton,
  BulkActionBar,
  StatusDot,
  InfiniteScrollSentinel,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

export function CurrenciesManagementPage() {
  const { t, i18n } = useTranslation();
  const isRTL = useRTL();
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteCurrency();
  const { openConfirm } = useModal();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editCurrency, setEditCurrency] = useState<Currency | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['currencies', 'infinite', search, statusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter;

      const { data } = await http.get<CurrencyListResponse>('/currencies', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.currencies.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allCurrencies = useMemo(
    () => data?.pages.flatMap((page) => page.currencies) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allCurrencies);

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allCurrencies.filter((c) => c.isActive).length,
    [allCurrencies]
  );
  const defaultCurrency = useMemo(
    () => allCurrencies.find((c) => c.isDefault),
    [allCurrencies]
  );

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('currencies.bulkDeleteTitle'),
      message: t('currencies.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        const results = await Promise.allSettled(
          Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id))
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) {
          toast.error(t('currencies.bulkDeletePartial', { failed }));
        } else {
          toast.success(t('currencies.bulkDeletedSuccess'));
        }
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* Header */}
      <PageHeader
        icon={<Coins className="w-7 h-7" />}
        title={t('currencies.management')}
        action={
          <CreateButton
            label={t('currencies.create')}
            onClick={() => setCreateModalOpen(true)}
          />
        }
      />

      {/* Stats Cards */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<Coins className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('currencies.total')}
          value={totalCount}
        />
        <StatCard
          icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('currencies.active')}
          value={activeCount}
        />
        {defaultCurrency && (
          <div
            className="flex items-center gap-3 p-3 md:p-4 rounded-xl glass-surface col-span-2 md:col-span-2"
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadow,
            }}
          >
            <Star className="w-7 h-7 md:w-8 md:h-8 flex-shrink-0" style={{ color: '#f59e0b' }} />
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase">{t('currencies.defaultCurrency')}</p>
              <p className="text-xl font-bold text-slate-800">
                {defaultCurrency.symbol} {defaultCurrency.code} — {localizedName(defaultCurrency, i18n.language)}
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Search & Filters */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h2 className="font-semibold text-xs sm:text-sm">
            {t('common.filters')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col md:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('currencies.searchPlaceholder')}
          />

          <button
            onClick={selectAll}
            className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={selectAll}
              className="w-5 h-5 pointer-events-none"
              readOnly
            />
            <span className="font-bold text-sm">{t('currencies.selectAll')}</span>
          </button>

          <GlassSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('currencies.allStatuses')}</option>
            <option value="true">{t('currencies.active')}</option>
            <option value="false">{t('currencies.inactive')}</option>
          </GlassSelect>
        </div>
      </motion.div>

      {/* List */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('currencies.loadError')} />
        ) : allCurrencies.length === 0 ? (
          <EmptyState
            icon={<Coins className="w-14 h-14 md:w-16 md:h-16" />}
            message={t('currencies.noCurrencies')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allCurrencies.map((currency) => {
              const selected = isSelected(currency.id);

              return (
              <motion.div
                key={currency.id}
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  triggerHapticFeedback();
                  setSelectedCurrency(currency);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    triggerHapticFeedback();
                    setSelectedCurrency(currency);
                  }
                }}
                className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 md:py-2 md:ps-6 md:pe-4 rounded-xl text-start cursor-pointer glass-surface"
                style={{
                  backgroundColor: ADMIN_COLORS.surface,
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  boxShadow: selected ? ADMIN_COLORS.shadowMd : ADMIN_COLORS.shadow,
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Checkbox */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                    style={{ minWidth: '44px', minHeight: '44px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(currency.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggle(currency.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 pointer-events-none"
                    />
                  </div>

                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center text-lg font-bold"
                    style={{
                      backgroundColor: currency.isActive ? '#ecfdf5' : '#f3f4f6',
                      border: `1px solid ${ADMIN_COLORS.border}`,
                    }}
                  >
                    {currency.symbol}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[200px] md:max-w-none">
                      {localizedName(currency, i18n.language)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span
                        className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                        style={{
                          backgroundColor: ADMIN_COLORS.surface,
                          border: `1px solid ${ADMIN_COLORS.border}`,
                        }}
                      >
                        {currency.code}
                      </span>
                      {currency.isDefault && (
                        <span
                          className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                          style={{
                            backgroundColor: '#fef3c7',
                            border: `1px solid ${ADMIN_COLORS.border}`,
                          }}
                        >
                          <Star className="w-3 h-3 inline-block mr-0.5" />
                          {t('currencies.default')}
                        </span>
                      )}
                      <span className="text-xs text-slate-500">
                        {currency.decimalPlaces} {t('currencies.decimals')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusDot
                    active={currency.isActive}
                    title={currency.isActive ? t('currencies.active') : t('currencies.inactive')}
                  />
                  <ChevronRight
                    className="w-5 h-5 text-slate-500"
                    style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                  />
                </div>
              </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Infinite scroll sentinel */}
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sentinelRef={sentinelRef}
      />

      {/* Modals */}
      <CurrencyFormModal
        isOpen={createModalOpen || !!editCurrency}
        onClose={() => {
          queryClient.refetchQueries({ queryKey: ['currencies'] });
          setCreateModalOpen(false);
          setEditCurrency(null);
        }}
        editData={editCurrency}
      />

      {selectedCurrency && (
        <CurrencyDetailModal
          currency={selectedCurrency}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['currencies'] }); setSelectedCurrency(null); }}
          onEdit={(c) => {
            setSelectedCurrency(null);
            setEditCurrency(c);
          }}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        count={count}
        onDelete={handleBulkDelete}
        onClear={clear}
        isDeleting={deleteMutation.isPending}
      />
    </motion.div>
  );
}
